import {
  type FailureClassification,
  type FailureContext,
} from "../../failures.js";
import { LlmTransportError, type LlmErrorCode } from "../../llm/types.js";

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === "object" && value !== null;
}

function errorChain(error: unknown): UnknownRecord[] {
  const chain: UnknownRecord[] = [];
  const seen = new Set<unknown>();
  let current: unknown = error;
  while (isRecord(current) && !seen.has(current)) {
    seen.add(current);
    chain.push(current);
    current = current["cause"];
  }
  return chain;
}

export function httpStatusOf(error: unknown): number | null {
  for (const item of errorChain(error)) {
    if (typeof item["status"] === "number") return item["status"];
    if (typeof item["statusCode"] === "number") return item["statusCode"];
  }
  return null;
}

export function systemCodeOf(error: unknown): string | undefined {
  for (const item of errorChain(error)) {
    if (typeof item["code"] === "string") return item["code"];
  }
  return undefined;
}

function errorNameOf(error: unknown): string | undefined {
  for (const item of errorChain(error)) {
    if (typeof item["name"] === "string") return item["name"];
  }
  return undefined;
}

function isRequestTimeout(error: unknown): boolean {
  const name = errorNameOf(error);
  return name === "AbortError"
    || name === "TimeoutError"
    || name === "RequestTimeoutError"
    || name === "APIConnectionTimeoutError";
}

function networkClassification(systemCode: string | undefined): FailureClassification {
  if (!systemCode) return { failure_type: "network", failure_code: "network_failed" };
  if (["ENOTFOUND", "EAI_AGAIN"].includes(systemCode)) {
    return { failure_type: "network", failure_code: "dns_failed" };
  }
  if (systemCode === "ECONNREFUSED") {
    return { failure_type: "network", failure_code: "connection_refused" };
  }
  if (["ECONNRESET", "EPIPE", "UND_ERR_SOCKET"].includes(systemCode)) {
    return { failure_type: "network", failure_code: "connection_reset" };
  }
  if (["ENETUNREACH", "EHOSTUNREACH"].includes(systemCode)) {
    return { failure_type: "network", failure_code: "network_unreachable" };
  }
  if (
    systemCode.startsWith("ERR_TLS_")
    || systemCode.startsWith("ERR_SSL_")
    || systemCode.startsWith("CERT_")
    || ["DEPTH_ZERO_SELF_SIGNED_CERT", "SELF_SIGNED_CERT_IN_CHAIN", "UNABLE_TO_VERIFY_LEAF_SIGNATURE"].includes(systemCode)
  ) {
    return { failure_type: "network", failure_code: "tls_failed" };
  }
  if (["ETIMEDOUT", "UND_ERR_CONNECT_TIMEOUT"].includes(systemCode)) {
    return { failure_type: "timeout", failure_code: "connect_timeout" };
  }
  return { failure_type: "network", failure_code: "network_failed" };
}

export function networkError(
  error: unknown,
  signal: AbortSignal | undefined,
  message: string,
  detail: string,
  context: FailureContext = {},
): LlmTransportError {
  if (signal?.aborted) {
    return new LlmTransportError("cancelled", message, {
      details: [detail],
      classification: { failure_type: "cancelled", failure_code: "cancelled" },
      context,
    });
  }
  const systemCode = systemCodeOf(error);
  const classification: FailureClassification = isRequestTimeout(error)
    ? { failure_type: "timeout", failure_code: "request_timeout" }
    : networkClassification(systemCode);
  const code: LlmErrorCode = classification.failure_type === "timeout" ? "timeout" : "network";
  return new LlmTransportError(code, message, {
    retryable: true,
    details: [detail],
    classification,
    context: { ...context, ...(systemCode ? { system_code: systemCode } : {}) },
  });
}

export function httpError(
  status: number,
  message: string,
  detail: string,
  context: FailureContext = {},
): LlmTransportError {
  let code: LlmErrorCode;
  let classification: FailureClassification;
  let retryable = false;
  if (status === 401) {
    code = "auth";
    classification = { failure_type: "authentication", failure_code: "authentication_failed" };
  } else if (status === 403) {
    code = "permission_denied";
    classification = { failure_type: "authorization", failure_code: "permission_denied" };
  } else if (status === 404) {
    code = "endpoint";
    classification = { failure_type: "provider", failure_code: "resource_not_found" };
  } else if (status === 408) {
    code = "timeout";
    classification = { failure_type: "timeout", failure_code: "request_timeout" };
    retryable = true;
  } else if (status === 413 && context.operation === "upload") {
    code = "request_failed";
    classification = { failure_type: "media", failure_code: "media_too_large" };
  } else if (status === 415 && context.operation === "upload") {
    code = "request_failed";
    classification = { failure_type: "media", failure_code: "unsupported_media" };
  } else if (status === 429) {
    code = "rate_limited";
    classification = { failure_type: "rate_limit", failure_code: "rate_limited" };
    retryable = true;
  } else if (status >= 500) {
    code = "request_failed";
    classification = { failure_type: "provider", failure_code: "provider_unavailable" };
    retryable = true;
  } else if (context.operation === "upload") {
    code = "request_failed";
    classification = { failure_type: "media", failure_code: "media_upload_failed" };
  } else {
    code = "request_failed";
    classification = { failure_type: "provider", failure_code: "request_rejected" };
  }
  return new LlmTransportError(code, message, {
    retryable,
    details: [`status: ${status}`, detail],
    classification,
    context: { ...context, http_status: status },
  });
}
